import { useEffect, useRef, useState } from 'react'
import { Link, Route, Routes, useLocation } from 'react-router-dom'
import { AuthContinuePage } from './AuthContinuePage'
import { loginUrl } from './api'
import { MyRunsPage, TesterPage } from './browse'
import { LabIndex, ModelPage, NotFound, PelicanLanding, ResultPage, RunPage, RunnerLanding, WatermelonLanding } from './pages'
import { RUNNER_PATH } from './format'
import { SCENES, SCENE_SLUGS, sceneOfPath, type SceneSlug } from './scenes'
import { useSignedIn } from './session'
import { LangToggle, ThemeToggle, useLang } from './prefs'
import { DICT, useT } from './i18n'

function ScrollManager() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)))
      if (el) { el.scrollIntoView(); return }
    }
    window.scrollTo(0, 0)
  }, [pathname, hash])
  return null
}

/** Unified Lab nav: the same top-level menu as the hub, linking back to it (full page). */
const LAB_NAV: Array<{ href: string; en: string; zh: string }> = [
  { href: '/lab/chat', en: 'Chat', zh: '对话' },
  { href: '/lab/explore', en: 'Agents', zh: 'Agent' },
  { href: '/lab/ai-videos', en: 'AI videos', zh: 'AI 视频' },
]
/** After the Leaderboards menu, as on the hub. */
const LAB_NAV_AFTER: Array<{ href: string; en: string; zh: string }> = [
  { href: '/lab/learn', en: 'Learn', zh: '学习' },
]



/** Which scene the current page belongs to (defaults to pelican-test). */
function sceneOf(pathname: string): SceneSlug {
  return sceneOfPath(pathname) ?? 'pelican-test'
}

/** Scene switcher: a button opening a dropdown of all live scenes. */
function SceneSwitcher({ current, onNavigate }: { current: SceneSlug; onNavigate?: () => void }) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onPointer); document.removeEventListener('keydown', onKey) }
  }, [open ])
  return (
    <div className="scene-switcher" ref={root}>
      <button
        type="button"
        className="scene-switcher-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {t.header.leaderboards} <span aria-hidden="true" className="caret">▾</span>
      </button>
      {open ? (
        <div className="scene-menu" role="menu" aria-label={t.header.switchScene}>
          <a href="/lab/evals" role="menuitem" onClick={() => setOpen(false)}>{t.header.allBoards}</a>
          {SCENE_SLUGS.map((slug) => (
            <Link
              key={slug}
              to={SCENES[slug].path}
              role="menuitemradio"
              aria-checked={slug === current}
              className={slug === current ? 'active' : undefined}
              onClick={() => { setOpen(false); onNavigate?.() }}
            >
              {SCENES[slug].name}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function Header() {
  const signedIn = useSignedIn()
  const { pathname } = useLocation()
  const { lang, setLang } = useLang()
  const u = DICT[lang].header
  const [drawerOpen, setDrawerOpen] = useState(false)
  const closeDrawer = () => setDrawerOpen(false)

  useEffect(() => { closeDrawer() }, [pathname])
  useEffect(() => {
    if (!drawerOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      closeDrawer()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [drawerOpen])

  const currentPath = `${pathname}${location.hash}`
  const signIn = loginUrl(currentPath)
  return (
    <>
      <a href="/lab/ideas" className="announcement" aria-label={u.announceAria}>
        <span aria-hidden="true" className="announcement-wipe" />
        <span className="announcement-name">{u.announceName}</span>
        <span className="announcement-message">{u.announceMessage}</span>
        <span className="announcement-cta">{u.announceCta}</span>
      </a>
      <header className="site-header">
        <div className="header-bar">
          <div className="brand-lockup">
            <a href="/" className="site-logo" aria-label={u.home}>
              <img src="https://sandbase.ai/icon.svg" alt="" width={28} height={28} />
              <span>SandBase</span>
            </a>
            <span className="brand-divider" aria-hidden="true">/</span>
            <Link to="/lab" className="lab-tag">Lab</Link>
          </div>
          <nav className="desktop-nav" aria-label={u.primaryNav}>
            {LAB_NAV.map((item) => <a key={item.href} href={item.href}>{lang === 'zh-CN' ? item.zh : item.en}</a>)}
            <SceneSwitcher current={sceneOf(pathname)} />
            {LAB_NAV_AFTER.map((item) => <a key={item.href} href={item.href}>{lang === 'zh-CN' ? item.zh : item.en}</a>)}
            {signedIn ? <Link to={`${SCENES[sceneOf(pathname)].path}/me`} aria-current={pathname.endsWith('/me') ? 'page' : undefined}>{u.myRuns}</Link> : null}
          </nav>
          <div className="desktop-actions">
            <ThemeToggle lang={lang} />
            <LangToggle lang={lang} setLang={setLang} />
            {signedIn
              ? <a href="/console" className="console-link">{u.console}</a>
              : <a href={signIn} className="start-building">{u.signIn}</a>}
          </div>
          <button type="button" className="mobile-menu-button" aria-label={drawerOpen ? u.close : u.menu} aria-expanded={drawerOpen} onClick={() => setDrawerOpen((open) => !open)}>{drawerOpen ? u.close : u.menu}</button>
        </div>
      </header>
      {drawerOpen ? (
        <div className="mobile-drawer-root" role="dialog" aria-modal="true" aria-label={u.mobileNav}>
          <button type="button" className="mobile-drawer-backdrop" aria-label={u.closeMenu} onClick={closeDrawer} />
          <div className="mobile-drawer">
            <nav className="mobile-nav" aria-label={u.mobileNav}>
              {SCENE_SLUGS.map((slug) => (
                <Link key={slug} to={SCENES[slug].path} onClick={closeDrawer}>{SCENES[slug].name}</Link>
              ))}
              {signedIn ? <Link to={`${SCENES[sceneOf(pathname)].path}/me`} onClick={closeDrawer}>{u.myRuns}</Link> : null}
              {[...LAB_NAV, { href: '/lab/evals', en: 'Leaderboards', zh: '榜单' }, ...LAB_NAV_AFTER].map((item) => <a key={item.href} href={item.href} onClick={closeDrawer}>{lang === 'zh-CN' ? item.zh : item.en}</a>)}
            </nav>
            <div className="mobile-drawer-actions">
              <div className="row"><ThemeToggle lang={lang} /><LangToggle lang={lang} setLang={setLang} /></div>
              <a href={signedIn ? '/console' : signIn} onClick={closeDrawer}>{signedIn ? u.console : u.signIn}</a>

            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

function Footer() {
  const t = useT()
  const f = t.footer
  return (
    <footer className="site-footer">
      <div className="footer-container">
        <div className="footer-main">
          <div className="footer-brand">
            <a href="/" className="site-logo" aria-label={f.home}><img src="https://sandbase.ai/icon.svg" alt="" width={28} height={28} /><span>SandBase</span></a>
            <p>{f.blurb}</p>
          </div>
          <div className="footer-columns">
            <div><b>{f.explore}</b><a href="/models">{f.models}</a><a href="/apis">{f.apis}</a><a href="/agents">{f.agents}</a></div>
            <div><b>{f.build}</b><a href="/landing/sandbox">{f.sandbox}</a><a href="/docs/">{f.docs}</a><a href="/pricing">{f.pricing}</a></div>
            <div><b>{f.lab}</b><Link to="/lab/pelican-test">Pelican Test</Link><Link to={RUNNER_PATH}>Endless Runner</Link><Link to={SCENES['watermelon-test'].path}>Watermelon Test</Link><Link to="/lab/pelican-test#leaderboard">{f.leaderboard}</Link><Link to="/lab/pelican-test/me">{f.myRuns}</Link></div>
          </div>
        </div>
        <div className="footer-bottom"><span>{f.rights(new Date().getFullYear())}</span><a href="https://status.sandbase.ai" target="_blank" rel="noopener noreferrer"><i />{f.status}</a></div>
      </div>
    </footer>
  )
}

export function App() {
  const t = useT()
  return (
    <>
      <a href="#main" className="skip-link">{t.header.skip}</a>
      <ScrollManager />
      <Header />
      <main id="main">
        <Routes>
          <Route path="/lab/auth/continue" element={<AuthContinuePage />} />
          <Route path="/lab" element={<LabIndex />} />
          <Route path="/lab/pelican-test" element={<PelicanLanding />} />
          <Route path="/lab/pelican-test/runs/:id" element={<RunPage scene="pelican-test" />} />
          <Route path="/lab/pelican-test/r/:id" element={<ResultPage scene="pelican-test" />} />
          <Route path="/lab/pelican-test/m/:slug" element={<ModelPage scene="pelican-test" />} />
          <Route path="/lab/pelican-test/u/:name" element={<TesterPage scene="pelican-test" />} />
          <Route path="/lab/pelican-test/me" element={<MyRunsPage scene="pelican-test" />} />
          <Route path="/lab/endless-runner" element={<RunnerLanding />} />
          <Route path="/lab/endless-runner/runs/:id" element={<RunPage scene="endless-runner" />} />
          <Route path="/lab/endless-runner/r/:id" element={<ResultPage scene="endless-runner" />} />
          <Route path="/lab/endless-runner/m/:slug" element={<ModelPage scene="endless-runner" />} />
          <Route path="/lab/endless-runner/u/:name" element={<TesterPage scene="endless-runner" />} />
          <Route path="/lab/endless-runner/me" element={<MyRunsPage scene="endless-runner" />} />
          <Route path="/lab/watermelon-test" element={<WatermelonLanding />} />
          <Route path="/lab/watermelon-test/runs/:id" element={<RunPage scene="watermelon-test" />} />
          <Route path="/lab/watermelon-test/r/:id" element={<ResultPage scene="watermelon-test" />} />
          <Route path="/lab/watermelon-test/m/:slug" element={<ModelPage scene="watermelon-test" />} />
          <Route path="/lab/watermelon-test/u/:name" element={<TesterPage scene="watermelon-test" />} />
          <Route path="/lab/watermelon-test/me" element={<MyRunsPage scene="watermelon-test" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </>
  )
}
